<template>
  <div class='bucket-issue-body'>
    <div class="description-block" :style="`flex-wrap: ${isShowAll ? 'wrap' : 'nowrap'};`">
      <div class="data-title">{{$t('detailed.issue.description.title')}}</div>
      <div class="text-container description" :class='{closed: !isShowAll}'>
        {{description}}
      </div>
      <span class='hide-description-btn' @click.stop="isShowAll = !isShowAll">
          {{ captionOfDescriptionToggleButton }}
      </span>
    </div>
    <div class="asset-block">
      <div class="data-title">{{$t('detailed.issue.asset.title')}}</div>
      <v-badge
        tile
        overlap
        :icon="badgeIcon"
        :content="badgeContent"
        class="manual-config-badge"
        :class="{'copied-badge': displayedCopiedBadge}"
        offset-y='4px'
        :value="displayedCopiedBadge"
      >
        <div class="text-container asset-data-text" @click='copyAsset'>{{asset}}</div>
      </v-badge>
    </div>
    <div class="tld-block">
      <div class="data-title">{{$t('detailed.issue.tld.title')}}</div>
      <div class="text-container">
        {{tld}}
      </div>
    </div>
    <span class="hackers-view-block" v-if='currentSource === allBucketSources.aws && hackersView'>
      <div class="data-title">{{$t('detailed.issue.hackers.view.title')}}</div>
      <div class="text-container description">{{awsHackersView.text}}</div>
        <div v-for='(url, index) in awsHackersView.links' :key='index'>
         <div class='url-option'>
          <div class='data-title option'>
            <span class="material-icons bullet">
              lens_blur
            </span>
          </div>
         <div class='text-container url-option-link'>
            <a :href='url' target="_blank"> {{url}} </a>
         </div>
        </div>
      </div>
    </span>
    <span class="hackers-view-block" v-else-if='currentSource === allBucketSources.azure && hackersView'>
      <div class="data-title">{{$t('detailed.issue.hackers.view.title')}}</div>
      <div class="text-container description">{{azureHackersView.startText}}</div>
       <div class='url-option'>
        <div class='data-title option'>
          <span class="material-icons bullet">
            lens_blur
          </span>
        </div>
       <div class='text-container url-option-link'>{{azureHackersView.link}}</div>
      </div>
      <div class="text-container description">{{azureHackersView.endText}}</div>
    </span>
    <span class="hackers-view-block" v-else-if='currentSource === allBucketSources.gcp && hackersView'>
      <div class="data-title">{{$t('detailed.issue.hackers.view.title')}}</div>
      <div class="text-container description">{{gcpHackersView.texts[0]}}</div>
      <div class='text-container' v-html="gcpHackersView.codes[0]"></div>
      <div class="text-container description">{{gcpHackersView.texts[1]}}</div>
      <div class='text-container' v-html="gcpHackersView.codes[1]"></div>
      <div class="text-container description">{{gcpHackersView.texts[2]}}</div>
      <div class='text-container' v-html="gcpHackersView.codes[2]"></div>
      <div class="text-container description">{{gcpHackersView.texts[3]}}</div>
      <div class='text-container' v-html="gcpHackersView.codes[3]"></div>
      <div class="text-container description">{{gcpHackersView.texts[4]}}</div>
    </span>
    <span class="mitigation-block" v-if='currentSource === allBucketSources.aws && mitigationData'>
      <div class="data-title">{{$t('detailed.issue.mitigation.title')}}</div>
      <div class="text-container description">{{awsMitigationData.text}}</div>
        <div v-for='(item, index) in awsMitigationData.links' :key='index'>
         <div class='mitigation-option'>
          <div class='data-title option'>
            <span class="material-icons bullet">
              lens_blur
            </span>
          </div>
           <div class="mitigation-option-container">
             <span class="mitigation-title">{{item.split(':')[0]}}:</span>
             <span class='text-container'>{{item.split(':')[1]}}</span>
           </div>
        </div>
      </div>
    </span>
    <span class="mitigation-block" v-if='currentSource === allBucketSources.azure && mitigationData'>
      <div class="data-title">{{$t('detailed.issue.mitigation.title')}}</div>
      <div class="text-container description">{{azureMitigationData.title}}</div>
        <div class="mitigation-section-container" v-for='(mitigationSection, index) in azureMitigationData.data' :key='index'>
          <div class="data-sub-title">{{azureMitigationDataTitles[index]}}</div>
          <div v-for='(mitigation, key) in mitigationSection' :key='key'>
             <div class='mitigation-option'>
              <div class='data-title option'>
                <span class="material-icons bullet">
                  lens_blur
                </span>
              </div>
               <div class="mitigation-option-container">
                <div class='text-container'>{{mitigation}}</div>
               </div>
            </div>
          </div>
      </div>
    </span>
    <span class="mitigation-block" v-if='currentSource === allBucketSources.gcp && mitigationData'>
      <div class="data-title">{{$t('detailed.issue.mitigation.title')}}</div>
      <div class="text-container description">{{gcpMitigationData.firstSection.title}}</div>
      <div v-for='(mitigation, key) in gcpMitigationData.firstSection.data' :key='key'>
         <div class='mitigation-option'>
          <div class='data-title option'>
            <span class="material-icons bullet">
              lens_blur
            </span>
          </div>
           <div class="mitigation-option-container">
            <div class='text-container'>{{mitigation}}</div>
           </div>
        </div>
      </div>
      <div class="data-sub-title">{{gcpMitigationDataTitles[0]}}</div>
      <div class="text-container description">{{gcpMitigationData.secondSection.title}}</div>
      <div v-for='(mitigation, key) in gcpMitigationData.secondSection.data' :key='key'>
         <div class='mitigation-option'>
          <div class='data-title option'>
            <span class="material-icons bullet">
              lens_blur
            </span>
          </div>
           <div class="mitigation-option-container">
            <div class='text-container'>{{mitigation}}</div>
           </div>
        </div>
      </div>
      <div class="data-sub-title">{{gcpMitigationDataTitles[1]}}</div>
      <div class="text-container description">{{gcpMitigationData.thirdSection.title}}</div>
      <div v-for='(mitigation, key) in gcpMitigationData.thirdSection.data' :key='key'>
         <div class='mitigation-option'>
          <div class='data-title option'>
            <span class="material-icons bullet">
              lens_blur
            </span>
          </div>
           <div class="mitigation-option-container">
            <div class='text-container' v-html="mitigation"></div>
           </div>
        </div>
      </div>
    </span>
    <div class="technical-block" v-if="mitigationUrl">
      <div class="data-title">{{$t('issueDataPopup.technical.data')}}</div>
      <div class="technical-data">
        <div class='text-container'>
          <div class='details-column technical-data'>
            <div class="data-sub-title">{{$t('issueDataPopup.url')}}</div>
            <div class='text-container cve-url' :title='mitigationUrl'>
              <a :href='mitigationUrl' target="_blank"> {{mitigationUrl.replace(/%20/g, '-')}} </a>
            </div>
          </div>
        </div>
      </div>
    </div>
  </div>
</template>

<script>
import { CONFIGURATION } from '@/constants/configuration';

export default {
  name: 'BucketIssueBody',
  props: {
    issue: {
      type: Object,
      required: true,
    },
  },
  data() {
    return {
      displayedCopiedBadge: false,
      isShowAll: false,
    };
  },
  computed: {
    tld() {
      return this.issue.tld;
    },
    asset() {
      return this.issue.asset.replace(/__/g, '.');
    },
    metaData() {
      return this.issue.metaData;
    },
    description() {
      return this.issue.description;
    },
    badgeIcon() {
      return 'build';
    },
    badgeContent() {
      return this.$t('copied');
    },
    mitigationUrl() {
      return this.metaData.mitigation !== '' ? this.metaData.mitigation : null;
    },
    captionOfDescriptionToggleButton() {
      return this.isShowAll ? this.$t('detailed.issue.show.less.list') : this.$t('detailed.issue.show.more.list');
    },
    hackersView() {
      return this.issue.metaData?.hackersView || null;
    },
    awsHackersView() {
      return {text: this.hackersView[0], links: this.hackersView.slice(-2)};
    },
    azureHackersView() {
      return {startText: this.hackersView[0], link: this.hackersView[1], endText: this.hackersView[2]};
    },
    gcpHackersView() {
      return {
        texts: [this.hackersView[0], this.hackersView[2], this.hackersView[4], this.hackersView[6], this.hackersView[8]],
        codes: [this.hackersView[1], this.hackersView[3], this.hackersView[5], this.hackersView[7]],
      };
    },
    mitigationData() {
      return this.metaData?.mitigationData || null;
    },
    awsMitigationData() {
      return {text: this.mitigationData[0], links: this.mitigationData.slice(-2)};
    },
    azureMitigationData() {
      return {title: this.mitigationData[0], data: this.mitigationData.slice(1)};
    },
    azureMitigationDataTitles() {
      return [this.$t('cloud.bucket.issue.mitigation.data.protection'),
        this.$t('cloud.bucket.issue.mitigation.identity.access'),
        this.$t('cloud.bucket.issue.mitigation.networking'),
        this.$t('cloud.bucket.issue.mitigation.monitoring.logging')];
    },
    gcpMitigationData() {
      return {
        firstSection: {title: this.mitigationData[0], data: this.mitigationData[1]},
        secondSection: {title: this.mitigationData[2], data: this.mitigationData[3]},
        thirdSection: {title: this.mitigationData[4], data: this.mitigationData[5]},
      };
    },
    gcpMitigationDataTitles() {
      return [this.$t('cloud.bucket.issue.mitigation.gcp.console'),
        this.$t('cloud.bucket.issue.mitigation.cli.command')];
    },
    currentSource() {
      return this.metaData.assetSource;
    },
    allBucketSources() {
      return CONFIGURATION.SOURCE_TYPE;
    },
  },
  methods: {
    async copyAsset() {
      const data = this.asset;
      await navigator.clipboard.writeText(data);
      this.displayedCopiedBadge = true;
      await new Promise(resolve => setTimeout(resolve, 2000));
      this.displayedCopiedBadge = false;
    },
  },
};
</script>

<style lang='scss' scoped>
@import '~@/assets/styles/design-system';

.bucket-issue-body {
  .details-column {
    display: inline-block;
    width: fit-content;
    max-width: rem(650);
    margin-right: $space-4;
  }
  .description-block {
    display: block;
    width: rem(650);
    margin-bottom: $space-5;
    .text-container::first-letter {
      text-transform: capitalize;
    }
    .text-container {
      padding-right: $space-15;
      font-size: $font-size-base;
      letter-spacing: 0;
      line-height: rem(18);
      &.closed {
        display: -webkit-box;
        -webkit-box-orient: vertical;
        overflow: hidden;
        text-overflow: ellipsis;
        -webkit-line-clamp: 3;
        line-height: 1.2em;
        height: 3.6em;
      }
    }
  }
  .technical-block {
    padding-bottom: $space-5;
    display: block;
    padding-top: $space-4;
    .technical-data {
      padding-top: $space-1;
      .url {
        white-space: nowrap;
        overflow: hidden;
        text-overflow: ellipsis;
      }
    }
  }
  .hackers-view-block {
    display: block;
    width: rem(650);
    .bullet {
      flex: 1;
    }
    .text-container {
      padding-right: $space-15;
      color: var(--text-primary);
      font-size: $font-size-base;
      letter-spacing: 0;
      line-height: rem(18);
    }
    .url-option {
      display: flex;
      .url-option-link {
        padding-left: rem(5);
      }
    }
    .material-icons {
      font-size: rem(15);
      opacity: 0.5;
      margin-top: rem(3);
    }
  }
  .mitigation-block {
    display: block;
    padding-top: $space-5;
    width: rem(650);
    .bullet {
      flex: 1;
    }
    .text-container {
      padding-right: $space-15;
      color: var(--text-primary);
      font-size: $font-size-base;
      letter-spacing: 0;
      line-height: rem(18);
      padding-bottom: $space-4;
    }
    .mitigation-option {
      display: flex;
      .mitigation-option-container {
        display: inline-block;
        padding-left: rem(5);
        .mitigation-title {
          font-weight: bold;
          font-size: rem(13);
        }
      }
    }
    .mitigation-section-container {
      padding-top: $space-2;
    }
    .material-icons {
      font-size: rem(15);
      opacity: 0.5;
      margin-top: rem(3);
    }
    .description {
      white-space: pre-line;
    }
  }
  .data-sub-title {
    font-size: $font-size-xs;
    color: var(--text-secondary);
    letter-spacing: 0.8px;
    line-height: rem(10);
    text-transform: uppercase;
    margin-bottom: $space-1;
  }
  .data-title {
    color: var(--text-secondary);
    font-size: $font-size-sm;
    letter-spacing: 0.8px;
    line-height: rem(10);
    text-transform: uppercase;
    margin-bottom: $space-1;
  }
  .asset-block {
    margin-bottom: $space-5;
    width: rem(650);
    .manual-config-badge {
      max-width: 100%;
      line-height: 1.5;
      &:hover {
        cursor: pointer;
      }
      .asset-data-text {
        max-width: 100%;
        white-space: nowrap;
        overflow: hidden;
        text-overflow: ellipsis;
      }
      .copied {
        text-transform: capitalize;
        color: var(--brand-primary);
      }
    }
  }
  .tld-block {
    margin-bottom: $space-5;
  }
  .description {
    white-space: pre-line;
  }
  .hide-description-btn {
    color: var(--brand-primary);
    font-family: $font-family-regular;
    font-size: $font-size-base;
    letter-spacing: 0;
    &:hover {
      cursor: pointer;
    }
  }
}
</style>

<style lang='scss'>
@import '~@/assets/styles/design-system';

.bucket-issue-body {
  .manual-config-badge {
    .v-badge__wrapper {
      .v-badge__badge {
        background: transparent;
        border: none;
        color: var(--brand-primary);
        .v-icon {
          font-size: rem(16);
          transform: rotate(180deg);
        }
      }
    }
    &.copied-badge {
      .v-badge__wrapper {
        .v-badge__badge {
          text-transform: capitalize;
          background: transparent;
          border: none;
          color: var(--brand-primary);
          right: rem(20);
          inset: auto auto calc(100% - #{rem(1)}) calc(100% - #{rem(30)});
        }
      }
    }
  }
}
</style>
